import type { FlowByPhaseDataOverTime } from '../../api/work-periods-client/work-periods-client.type';
import type { Workflow } from '../../api/workflows-client/workflows-client.type';
import {
  blueBase,
  blueLight,
  blueLighter,
  graphAmethyst,
  graphArgentinianBlue,
  graphButterscotch,
  graphCambridgeBlue,
  graphChefchaouenBlue,
  graphDodgerBlue,
  graphKhaki,
  graphLightRed,
  graphMint,
  graphMoonstone,
  graphOldRose,
  graphPacificCyan,
  graphPictonBlue,
  graphPistachio,
  graphPurple,
  graphSandyBrown,
  graphSilverLakeBlue,
  graphSunglow,
  graphTropicalIndigo,
  graphVerdigris,
  primaryBase,
  primaryLight,
  primaryLighter,
  purpleBase,
  purpleLight,
  purpleLighter,
  redBase,
  redLight,
  secondaryBase,
  secondaryLight,
  secondaryLighter,
  yellowBase,
  yellowLight,
} from '../../styles/design-tokens';
import { CANONICAL_PHASE_KEYS, STEP_NAME_TO_PHASE_KEY } from '../phase-labels/phase-labels.type';
import { SUBGROUP_COLORS } from './subgroup-helpers.constants';

/**
 * Normalizes a status name for comparison.
 * - Converts to lowercase
 * - Replaces ALL Unicode whitespace (including non-breaking spaces) with regular spaces
 * - Collapses multiple whitespace characters into single spaces
 * - Trims leading/trailing whitespace
 *
 * @param {string} name - The status name to normalize.
 * @returns {string} Normalized status name.
 */
const normalizeStatusName = (name: string): string => {
  // Replace all Unicode whitespace characters with regular space
  // This handles regular spaces, non-breaking spaces, and other Unicode whitespace
  return name
    .toLowerCase()
    .replace(/[\s\u00A0\u2000-\u200B\u202F\u205F\u3000]+/g, ' ')
    .trim();
};

/**
 * Canonical step rank keyed by NORMALIZED step name, so a differently cased or
 * whitespace-padded `Step.name` still resolves to its canonical position.
 * Built once — `STEP_NAME_TO_PHASE_KEY` is a module constant.
 */
const CANONICAL_STEP_RANK_BY_NORMALIZED_NAME = new Map<string, number>(
  Object.entries(STEP_NAME_TO_PHASE_KEY).map(([stepName, phaseKey]) => [
    normalizeStatusName(stepName),
    CANONICAL_PHASE_KEYS.indexOf(phaseKey),
  ]),
);

/**
 * Finds the workflow that applies to a given subproject.
 *
 * @param {Workflow[]} workflows - Array of workflows to search through.
 * @param {string | null} subprojectId - The subproject ID to find a workflow for.
 * @returns {Workflow | undefined} The workflow that applies to the subproject, or undefined if not found.
 */
const findWorkflowForSubproject = (workflows: Workflow[], subprojectId: string | null): Workflow | undefined => {
  if (!subprojectId || !workflows || workflows.length === 0) {
    return undefined;
  }

  return workflows.find((workflow) => workflow.subprojects?.includes(subprojectId));
};

/**
 * Extracts ordered status names from a workflow.
 * Returns statuses in the order defined by the workflow steps and their mappings.
 *
 * @param {Workflow | undefined} workflow - The workflow to extract statuses from.
 * @returns {string[]} Array of status names ordered by step order_index, then mapping order_index.
 */
const getOrderedStatusesFromWorkflow = (workflow: Workflow | undefined): string[] => {
  if (!workflow || !workflow.steps) {
    return [];
  }

  // Sort steps by order_index
  const sortedSteps = [...workflow.steps].sort((a, b) => a.order_index - b.order_index);

  // Extract mappings from each step, sorted by order_index
  const orderedStatuses: string[] = [];
  for (const step of sortedSteps) {
    if (step.mappings && step.mappings.length > 0) {
      const sortedMappings = [...step.mappings].sort((a, b) => a.order_index - b.order_index);
      for (const mapping of sortedMappings) {
        if (mapping.external_name && !orderedStatuses.includes(mapping.external_name)) {
          orderedStatuses.push(mapping.external_name);
        }
      }
    }
  }

  return orderedStatuses;
};

/**
 * Merges the ordered status lists of several workflows into a single ordered
 * list, bucketing by workflow step rather than concatenating whole workflows.
 *
 * A strategy initiative can span boards governed by different workflows, so
 * `getOrderedStatusesFromWorkflow` alone is not enough: concatenating its output
 * per workflow would place the second workflow's backlog statuses after the
 * first workflow's done statuses. Bucketing by step rank first keeps every
 * workflow's backlog statuses together, then every workflow's next step, so the
 * merged list still reads start-of-process to end-of-process.
 *
 * Steps are ranked by canonical step name, NOT by the stored
 * `Step.order_index`: `data-management.helpers.ts` writes `Waiting: 0,
 * Backlog: 1`, so sorting on the stored index would order sub-group charts
 * Waiting-then-Backlog while grouped charts follow `CANONICAL_PHASE_KEYS` and
 * order Backlog-then-Waiting. The name lookup is normalized, so differently
 * cased or padded step names still resolve canonically rather than silently
 * falling back to the inverted stored scale.
 *
 * Steps whose name is not canonical always rank after every canonical step;
 * their stored index only orders them relative to each other, and is coerced
 * because a negative, non-numeric, or missing value would otherwise land inside
 * the canonical range (`7 + -1` collides with the Done bucket, `7 + undefined`
 * is `NaN`, which `Array#sort` leaves in place).
 *
 * A status mapped under different steps by different workflows resolves to the
 * EARLIEST of those step ranks, because ranks are walked in ascending order and
 * the first appearance wins.
 *
 * @param {Workflow[]} workflows - Workflows whose statuses should be merged.
 * @returns {string[]} Status `external_name`s ordered by canonical step, then
 *   by mapping order within the step, de-duplicated on first appearance (case-
 *   and whitespace-insensitively, matching `sortPhaseKeysByWorkflow`).
 */
const getOrderedStatusesFromWorkflows = (workflows: Workflow[]): string[] => {
  if (!workflows || workflows.length === 0) {
    return [];
  }

  const mappingsByStepRank = new Map<number, string[]>();
  // Unknown step names always rank after every canonical step. The stored index
  // is clamped and coerced first: it is untrusted, and a negative or missing
  // value added to the canonical length would land inside the canonical range
  // instead of after it.
  const rankOf = (step: { name?: string; order_index?: number }): number => {
    const canonicalRank = CANONICAL_STEP_RANK_BY_NORMALIZED_NAME.get(normalizeStatusName(step.name ?? ''));
    if (canonicalRank !== undefined) {
      return canonicalRank;
    }
    const storedIndex = Number(step.order_index);
    return CANONICAL_PHASE_KEYS.length + (Number.isFinite(storedIndex) ? Math.max(storedIndex, 0) : 0);
  };

  for (const workflow of workflows) {
    for (const step of workflow?.steps ?? []) {
      const stepRank = rankOf(step);
      const bucket = mappingsByStepRank.get(stepRank) ?? [];
      const sortedMappings = [...(step.mappings ?? [])].sort((a, b) => a.order_index - b.order_index);

      for (const mapping of sortedMappings) {
        if (mapping.external_name) {
          bucket.push(mapping.external_name);
        }
      }

      mappingsByStepRank.set(stepRank, bucket);
    }
  }

  const seen = new Set<string>();
  const orderedStatuses: string[] = [];

  for (const stepRank of [...mappingsByStepRank.keys()].sort((a, b) => a - b)) {
    for (const externalName of mappingsByStepRank.get(stepRank) ?? []) {
      const normalized = normalizeStatusName(externalName);
      if (!seen.has(normalized)) {
        seen.add(normalized);
        orderedStatuses.push(externalName);
      }
    }
  }

  return orderedStatuses;
};

/**
 * Sorts phase keys according to workflow ordering.
 * Keys found in the workflow are sorted by their workflow order.
 * Keys not in the workflow are appended at the end in their original order.
 *
 * @param {string[]} keys - Array of phase keys to sort.
 * @param {string[]} workflowOrder - Array of status names in the desired order from workflow.
 * @returns {string[]} Sorted array of phase keys with workflow keys first, then leftover keys.
 */
const sortPhaseKeysByWorkflow = (keys: string[], workflowOrder: string[]): string[] => {
  if (!workflowOrder || workflowOrder.length === 0) {
    return keys;
  }

  // Create a map of normalized status name to its index in the workflow order
  const orderMap = new Map<string, number>();
  workflowOrder.forEach((status, index) => {
    orderMap.set(normalizeStatusName(status), index);
  });

  // Separate keys into those in the workflow and those not in the workflow
  const workflowKeys: string[] = [];
  const leftoverKeys: string[] = [];

  keys.forEach((key) => {
    if (orderMap.has(normalizeStatusName(key))) {
      workflowKeys.push(key);
    } else {
      leftoverKeys.push(key);
    }
  });

  // Sort workflow keys by their order in the workflow
  workflowKeys.sort((a, b) => {
    const aIndex = orderMap.get(normalizeStatusName(a)) ?? 0;
    const bIndex = orderMap.get(normalizeStatusName(b)) ?? 0;
    return aIndex - bIndex;
  });

  // Return workflow keys first, then leftover keys at the end
  return [...workflowKeys, ...leftoverKeys];
};

/**
 * Extracts unique phase/status keys from the flow by phase data.
 * Works for both groups (tasks_backlog, tasks_wip) and sub-groups (tasks_2a. in progress).
 *
 * @param {FlowByPhaseDataOverTime | undefined} data - The data to extract keys from.
 * @return {string[]} Array of unique phase/status identifiers (without tasks_/points_ prefix).
 */
const extractPhaseKeys = (data?: FlowByPhaseDataOverTime): string[] => {
  if (!data) {
    return [];
  }

  const keys = new Set<string>();
  Object.values(data).forEach((dayData) => {
    Object.keys(dayData).forEach((key) => {
      // Extract phase name by removing tasks_ or points_ prefix
      if (key.startsWith('tasks_')) {
        keys.add(key.replace('tasks_', ''));
      } else if (key.startsWith('points_')) {
        keys.add(key.replace('points_', ''));
      }
    });
  });

  return Array.from(keys);
};

/**
 * Maps a color to its color family for contrast checking.
 * Used to prevent similar colors from appearing consecutively.
 *
 * @param {string} color - The hex color code.
 * @return {string} The color family name.
 */
const getColorFamily = (color: string): string => {
  // Purple family
  if (
    color === purpleBase ||
    color === purpleLight ||
    color === purpleLighter ||
    color === graphPurple ||
    color === graphAmethyst
  ) {
    return 'purple';
  }
  // Blue family
  if (
    color === blueBase ||
    color === blueLight ||
    color === blueLighter ||
    color === graphChefchaouenBlue ||
    color === graphDodgerBlue ||
    color === graphPictonBlue ||
    color === graphPacificCyan ||
    color === graphSilverLakeBlue ||
    color === graphArgentinianBlue ||
    color === graphMoonstone ||
    color === secondaryBase ||
    color === secondaryLight ||
    color === secondaryLighter ||
    color === graphTropicalIndigo
  ) {
    return 'blue';
  }
  // Red family
  if (color === redBase || color === redLight || color === graphLightRed || color === graphOldRose) {
    return 'red';
  }
  // Yellow/Orange family
  if (
    color === yellowBase ||
    color === yellowLight ||
    color === graphSandyBrown ||
    color === graphButterscotch ||
    color === graphSunglow
  ) {
    return 'yellow';
  }
  // Green family
  if (
    color === primaryBase ||
    color === primaryLight ||
    color === primaryLighter ||
    color === graphCambridgeBlue ||
    color === graphPistachio ||
    color === graphVerdigris ||
    color === graphMint
  ) {
    return 'green';
  }
  // Neutral/unique
  return color === graphKhaki ? 'neutral' : 'unique';
};

/**
 * Creates a color mapping for all subgroups based on their keys.
 * Ensures each subgroup gets a unique color with maximum contrast distribution.
 *
 * Algorithm:
 * 1. Tracks all assigned colors to guarantee uniqueness (no duplicates)
 * 2. Cycles through ALL color families before reusing any family for better contrast
 * 3. Uses deterministic hashing for consistent color assignment per key
 *
 * @param {string[]} keys - Array of subgroup keys.
 * @return {Record<string, string>} Mapping of key to color.
 */
const createSubGroupColorMap = (keys: string[]): Record<string, string> => {
  const colorMap: Record<string, string> = {};
  const assignedColors = new Set<string>(); // Track all assigned colors for uniqueness
  const usedFamilies = new Set<string>(); // Track ALL used families for maximum contrast

  // Available color families: purple, blue, red, yellow, green, neutral, unique
  const allFamilies = ['purple', 'blue', 'red', 'yellow', 'green', 'neutral', 'unique'];

  keys.forEach((key) => {
    // Start with the hash-based color assignment for consistency
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      const char = key.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    let baseIndex = Math.abs(hash) % SUBGROUP_COLORS.length;
    let selectedColor = SUBGROUP_COLORS[baseIndex];
    let selectedFamily = getColorFamily(selectedColor);

    // First priority: Ensure color uniqueness (no duplicates)
    // Second priority: Cycle through all families before reusing any for maximum contrast
    let attempts = 0;
    const maxAttempts = SUBGROUP_COLORS.length * 2; // Try harder to find good color

    while (attempts < maxAttempts) {
      const colorAlreadyUsed = assignedColors.has(selectedColor);
      const familyAlreadyUsed = usedFamilies.has(selectedFamily);

      // Color must always be unique
      if (colorAlreadyUsed) {
        // Try next color
        baseIndex = (baseIndex + 1) % SUBGROUP_COLORS.length;
        selectedColor = SUBGROUP_COLORS[baseIndex];
        selectedFamily = getColorFamily(selectedColor);
        attempts++;
        continue;
      }

      // If all families haven't been used yet, prefer unused families
      if (usedFamilies.size < allFamilies.length) {
        if (!familyAlreadyUsed) {
          // Perfect: unique color from unused family
          break;
        }
        // Keep trying to find a color from an unused family
        if (attempts < SUBGROUP_COLORS.length) {
          baseIndex = (baseIndex + 1) % SUBGROUP_COLORS.length;
          selectedColor = SUBGROUP_COLORS[baseIndex];
          selectedFamily = getColorFamily(selectedColor);
          attempts++;
          continue;
        }
      }

      // All families have been used at least once, or we've tried enough
      // Accept any unique color at this point
      break;
    }

    // Assign color and track it
    colorMap[key] = selectedColor;
    assignedColors.add(selectedColor);
    usedFamilies.add(selectedFamily);

    // Reset family tracking once all families have been used
    // This allows cycling through families again for better distribution
    if (usedFamilies.size === allFamilies.length) {
      usedFamilies.clear();
    }
  });

  return colorMap;
};

export {
  createSubGroupColorMap,
  extractPhaseKeys,
  findWorkflowForSubproject,
  getOrderedStatusesFromWorkflow,
  getOrderedStatusesFromWorkflows,
  normalizeStatusName,
  sortPhaseKeysByWorkflow,
};
